import { useCallback, useMemo, useState } from 'react';
import { IncomingCallsRequest } from '../../../../../store/reducers/IncomingCallsSlice';
import { ReportRequest } from '../../../../../store/reducers/ReportSlice';
import { useIncomingFilterChanges } from '../useIncomingFilterChanges/useIncomingFilterChanges';

interface Props {
  onCallRequest: (value: IncomingCallsRequest) => void;
  callRequest: IncomingCallsRequest;
  onCallRequestReport: (value: ReportRequest) => void;
  callRequestReport: ReportRequest;
}

const hasActiveFilterValue = (value: unknown) => {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
};

export const useIncomingHandlersFilter = ({
  onCallRequest,
  callRequest,
  onCallRequestReport,
  callRequestReport,
}: Props) => {
  const {
    handleClinics,
    handleCallStatus,
    handleAttemptStatus,
    handleCallResult,
    handleScenario,
  } = useIncomingFilterChanges({
    onCallRequest,
    callRequest,
    onCallRequestReport,
    callRequestReport,
  });

  const [clinicValue, setClinicValue] = useState<string[]>([]);

  const handleClinicsChange = (value: string[]) => {
    setClinicValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleClinics(value);
    }
  };

  const [taskStatusValue, setTaskStatusValue] = useState<string[]>([]);
  const [attemptStatusValue, setAttemptStatusValue] = useState<string[]>([]);

  const handleTaskStatusChange = (value: string[]) => {
    setTaskStatusValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleCallStatus(value);
    }
  };

  const handleAttemptStatusChange = (value: string[]) => {
    setAttemptStatusValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleAttemptStatus(value);
    }
  };

  const [resultValue, setResultValue] = useState<string[]>([]);
  const [scenarioValue, setScenarioValue] = useState<string[]>([]);

  const isResetDisabled = useMemo(
    () =>
      ![
        clinicValue,
        taskStatusValue,
        attemptStatusValue,
        resultValue,
        scenarioValue,
        callRequest['Filter.ClinicIds'],
        callRequest['Filter.States'],
        callRequest['Filter.AttemptStates'],
        callRequest['Filter.Results'],
        callRequest['Filter.Types'],
        callRequest['Filter.FormTemplateIds'],
        callRequest['Filter.FormBatchIds'],
        callRequest['Filter.DispensarizationBatchIds'],
      ].some(hasActiveFilterValue),
    [
      clinicValue,
      taskStatusValue,
      attemptStatusValue,
      resultValue,
      scenarioValue,
      callRequest,
    ],
  );

  const handleResultChange = (value: string[]) => {
    setResultValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleCallResult(value);
    }
  };

  const handleScenarioChange = (value: string[]) => {
    setScenarioValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleScenario(value);
    }
  };

  const handleOpenClinics = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClinics(clinicValue);
      }
    },
    [handleClinics, clinicValue],
  );

  const handleOpenTaskStatus = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCallStatus(taskStatusValue);
      }
    },
    [handleCallStatus, taskStatusValue],
  );

  const handleOpenAttemptStatus = useCallback(
    (open: boolean) => {
      if (!open) {
        handleAttemptStatus(attemptStatusValue);
      }
    },
    [handleAttemptStatus, attemptStatusValue],
  );

  const handleOpenResult = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCallResult(resultValue);
      }
    },
    [handleCallResult, resultValue],
  );

  const handleOpenScenario = useCallback(
    (open: boolean) => {
      if (!open) {
        handleScenario(scenarioValue);
      }
    },
    [handleScenario, scenarioValue],
  );

  const resetFilters = useCallback(() => {
    if (isResetDisabled) return;

    setClinicValue([]);
    setTaskStatusValue([]);
    setAttemptStatusValue([]);
    setResultValue([]);
    setScenarioValue([]);
    onCallRequest({
      ...callRequest,
      PageIndex: 0,
      'Filter.ClinicIds': [],
      'Filter.States': [],
      'Filter.AttemptStates': [],
      'Filter.Results': [],
      'Filter.Types': [],
      'Filter.FormTemplateIds': [],
      'Filter.FormBatchIds': [],
      'Filter.DispensarizationBatchIds': [],
    });

    onCallRequestReport({
      ...callRequestReport,
      ClinicIds: [],
      States: [],
      AttemptStates: [],
      Results: [],
      Types: [],
      FormTemplateIds: [],
      FormBatchIds: [],
      DispensarizationBatchIds: [],
    });
  }, [
    isResetDisabled,
    onCallRequest,
    callRequest,
    onCallRequestReport,
    callRequestReport,
  ]);

  return {
    handleClinicsChange,
    clinicValue,
    handleOpenClinics,
    handleTaskStatusChange,
    taskStatusValue,
    handleOpenTaskStatus,
    handleAttemptStatusChange,
    attemptStatusValue,
    handleOpenAttemptStatus,
    handleScenarioChange,
    scenarioValue,
    handleOpenScenario,
    handleResultChange,
    resultValue,
    isResetDisabled,
    handleOpenResult,
    resetFilters,
  };
};
